import { type FC, useMemo, useState } from 'react';

import { Carousel as DibsCarousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { renderDot } from './CarouselDot';

type SwiperClasses = { [key in 'wrapper' | 'list' | 'prevArrow' | 'nextArrow' | 'item']?: string };
type CarouselClasses = {
    [key in
        | 'wrapper'
        | 'listWrapper'
        | 'list'
        | 'item'
        | 'arrow'
        | 'arrowSVG'
        | 'arrowLeft'
        | 'arrowRight'
        | 'dotsWrapper']?: string;
};

const swiperClassNameMap: Record<string, string> = {
    arrowLeft: 'prevArrow',
    arrowRight: 'nextArrow',
};
function mapSwiperClassNames(
    classNames?: Record<string, string | undefined> | undefined
): Record<string, string | undefined> | undefined {
    if (!classNames) return undefined;
    const remapped: Record<string, string | undefined> = {};
    Object.keys(classNames).forEach(key => {
        remapped[swiperClassNameMap[key] || key] = classNames[key];
    });

    return remapped;
}

type Props = {
    // useLazyLoadImages?: boolean;
    pageSetOffset?: boolean;
    itemsPerPage: number;
    renderItem: FC<{
        order?: number;
        index: number;
        isCurrentItem?: boolean;
        isVisible?: boolean;
    }>;
    count: number;
    isFullWidth?: boolean;
    showDots?: boolean;
    handlePageChange?: unknown;
    hideArrows?: boolean;
    isSwiper?: boolean;
    /** duration in ms */
    autoRunDuration?: number;
    /** listWrapper, arrow, arrowSVG, and dotsWrapper do not apply when isSwiper === true */
    classNames?: SwiperClasses | CarouselClasses;
};
/** a generic carousel and/or swiper */
export const Carousel: FC<Props> = ({
    // useLazyLoadImages,
    itemsPerPage,
    showDots,
    count,
    renderItem,
    handlePageChange,
    hideArrows,
    classNames,
    isSwiper,
    autoRunDuration,
}) => {
    const items = useMemo(() => Array(count).fill(renderItem), [count, renderItem]);
    const [isCarouselVisible, setIsCarouselVisible] = useState<boolean>(false);
    if (isSwiper) {
        return (
            <Swiper
                classNames={mapSwiperClassNames(classNames)}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
            >
                {items.map((RenderItem, index) => (
                    <RenderItem index={index} key={`swiper-item-${index}`} />
                ))}
            </Swiper>
        );
    }

    return (
        <>
            <VisibilityTracker
                watchAfterFirstVisible
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible !== isCarouselVisible) {
                        setIsCarouselVisible(isVisible);
                    }
                }}
            />
            <DibsCarousel
                autoRunDuration={autoRunDuration}
                classNames={classNames}
                isAutoRun={isCarouselVisible && count > 1}
                dataTn="hero-items-carousel"
                showDots={showDots}
                hideArrows={hideArrows}
                totalItems={count}
                itemsPerPage={itemsPerPage}
                step={itemsPerPage}
                renderItem={renderItem}
                renderDot={renderDot}
            />
        </>
    );
};
